import {
  ArrowUp, Sparkles, SquarePen, X,
} from 'lucide-react';
import {
  FC, KeyboardEvent, ReactElement, useEffect, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';

import { ds } from '@/config/colors';
import { useOrganizationState } from '@/state';
import { LocaleLink, useLocaleNavigate } from '@/utils/locale';

import { axiosInstance } from '../../../../axios';
import { aiPoolRequiredMessage, isAiPoolRequiredError } from '../../../../axios/aiLimit';
import { useWarmup } from '../../../../hooks/useWarmup';
import { StefanKbUpdates } from './StefanKbUpdates';

/**
 * Chat z asystentem AI panelu Hotelspot — osadzony bezpośrednio na Dashboardzie
 * (zastąpił sekcję "Co nowego"). Kompaktowy layout dopasowany do prawej kolumny
 * grida 70/30 — wąski, wysoki, z mniejszymi paddingami niż wersja drawerowa.
 *
 * Backend: `POST /v3-panelHelpChat` (codebase `ai`) z `{ message, history, organizationId, language }`.
 */

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
}

interface ApiResponse {
  success: boolean;
  data?: { answer: string };
  error?: string;
}

/* ============================================================
   Styles: układ jak czat „Zapytaj AI" w hotelspot-audit. Biały panel,
   nagłówek z iskrą, krótki opis, rozmowa bez awatarów i karta pisania
   na dole z podpowiedziami. Bez załączników.
   ============================================================ */

const Card = styled.div`
  display: flex;
  flex-direction: column;
  height: 460px;
  align-self: start;
  background: ${({ theme }) => (theme.mode === 'dark' ? '#0f1419' : '#ffffff')};
  border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#1f2937' : ds.borderDefault)};
  border-radius: 8px;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 28px 20px 0 24px;
`;

const HeaderTitle = styled.h2`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
  margin: 0;
  font-size: 20px;
  font-weight: 500;
  line-height: 1.3;
  letter-spacing: -0.01em;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#f3f4f6' : ds.textPrimary)};

  svg { width: 20px; height: 20px; stroke-width: 1.75; flex-shrink: 0; }
`;

/* Ikony w nagłówku (nowa rozmowa, zamknij): bez ramki, szare, ciemnieją na hover. */
const HeaderIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: ${ds.textSecondary};
  cursor: pointer;
  transition: color 0.15s ease, background-color 0.15s ease;

  &:hover:not(:disabled) {
    color: ${({ theme }) => (theme.mode === 'dark' ? '#ffffff' : ds.textPrimary)};
    background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : ds.bgMuted)};
  }

  &:disabled {
    color: ${ds.textMuted};
    cursor: default;
  }

  svg { width: 18px; height: 18px; stroke-width: 1.75; }
`;

const Intro = styled.p`
  margin: 16px 0 0;
  padding: 0 24px;
  font-size: 15px;
  line-height: 1.6;
  color: ${ds.textSecondary};
`;

const Messages = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 0;

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)')};
    border-radius: 3px;
  }
`;

const MessageRow = styled.div<{ $role: 'user' | 'assistant' }>`
  display: flex;
  max-width: 100%;
  align-self: ${({ $role }) => ($role === 'user' ? 'flex-end' : 'flex-start')};
`;

/* Wiadomość użytkownika w szarej chmurce, odpowiedź AI jako zwykły tekst. */
const Bubble = styled.div<{ $role: 'user' | 'assistant'; $error?: boolean }>`
  max-width: ${({ $role }) => ($role === 'user' ? '85%' : '100%')};
  padding: ${({ $role }) => ($role === 'user' ? '10px 14px' : '0')};
  border-radius: 16px;
  font-size: 14px;
  line-height: 1.6;
  word-wrap: break-word;
  background: ${({ $role, theme }) => {
    if ($role !== 'user') return 'transparent';
    return theme.mode === 'dark' ? '#1a2028' : ds.bgMuted;
  }};
  color: ${({ $error, theme }) => {
    if ($error) return '#dc2626';
    return theme.mode === 'dark' ? '#f3f4f6' : ds.textPrimary;
  }};

  strong { font-weight: 600; }

  a {
    color: inherit;
    font-weight: 500;
    text-decoration: underline;
    text-decoration-thickness: 1px;
    text-underline-offset: 3px;
    cursor: pointer;

    &:hover { opacity: 0.75; }
  }

  code {
    font-family: ui-monospace, 'SF Mono', Menlo, monospace;
    font-size: 12px;
    padding: 2px 5px;
    border-radius: 4px;
    background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(15, 23, 42, 0.06)')};
  }

  ol, ul {
    margin: 6px 0;
    padding-left: 18px;
  }
  li { margin: 2px 0; }
  p { margin: 0; }
  p + p { margin-top: 8px; }
`;

const ComposerArea = styled.div`
  flex-shrink: 0;
  padding: 0 24px 16px;
`;

/* Karta pisania: podpowiedzi u góry (tylko w pustej rozmowie), pod nimi pole i wyślij. */
const ComposerCard = styled.form`
  border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#374151' : ds.borderDefault)};
  border-radius: 16px;
  background: ${({ theme }) => (theme.mode === 'dark' ? '#0f1419' : '#ffffff')};
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.06);
  overflow: hidden;
  transition: border-color 0.15s ease;

  &:focus-within {
    border-color: ${ds.borderStrong};
  }
`;

const Suggestions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  padding: 16px;
  border-bottom: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#1f2937' : ds.borderDefault)};
`;

const SuggestionChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
  padding: 7px 14px;
  border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#374151' : ds.borderDefault)};
  border-radius: 10px;
  background: transparent;
  font-family: inherit;
  font-size: 14px;
  text-align: left;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#f3f4f6' : ds.textPrimary)};
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease;

  &:hover:not(:disabled) {
    border-color: ${ds.borderStrong};
    background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : ds.bgMuted)};
  }

  &:disabled { opacity: 0.5; cursor: not-allowed; }

  svg { width: 16px; height: 16px; stroke-width: 1.75; flex-shrink: 0; }
`;

const TextArea = styled.textarea`
  display: block;
  width: 100%;
  resize: none;
  border: none;
  padding: 14px 20px 4px;
  font-size: 15px;
  font-family: inherit;
  line-height: 1.5;
  max-height: 120px;
  min-height: 44px;
  background: transparent;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#f3f4f6' : ds.textPrimary)};
  outline: none;

  &::placeholder {
    color: ${ds.textMuted};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ComposerFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 4px 10px 10px;
`;

const SendButton = styled.button`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 999px;
  background: ${ds.accentInk};
  color: #ffffff;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover:not(:disabled) {
    background: ${ds.accentInkHover};
  }

  &:disabled {
    background: ${ds.borderStrong};
    cursor: not-allowed;
  }

  svg { width: 18px; height: 18px; stroke-width: 2; }
`;

const Disclaimer = styled.p`
  margin: 10px 0 0;
  font-size: 13px;
  line-height: 1.5;
  color: ${ds.textMuted};

  a {
    color: ${ds.textSecondary};
    text-decoration: underline;
    text-underline-offset: 3px;

    &:hover { color: ${ds.textPrimary}; }
  }
`;

const dotPulse = keyframes`
  0%, 80%, 100% { opacity: 0.2; transform: translateY(0); }
  40% { opacity: 1; transform: translateY(-2px); }
`;

const TypingDots = styled.div`
  display: inline-flex;
  gap: 4px;
  padding: 4px 0;

  span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.4)')};
    animation: ${dotPulse} 1.2s infinite ease-in-out;
    &:nth-child(2) { animation-delay: 0.2s; }
    &:nth-child(3) { animation-delay: 0.4s; }
  }
`;

/* ============================================================
   Markdown rendering — wspiera **bold**, `code`, [text](url), listy
   ============================================================ */

/**
 * Mapa ścieżka → ludzka nazwa. Używana w preprocessorze defensywnym do
 * konwersji "gołych" URL (np. /events/new bez markdown) na klikalne linki.
 * Lista pochodzi z menuConfig — keep in sync gdy panel zmieni routing.
 */
const PATH_LABELS: Record<string, string> = {
  '/': 'Dashboard',
  '/analytics': 'Analityka',
  '/events': 'Wydarzenia',
  '/events/all-events': 'Wydarzenia',
  '/events/new': 'Utwórz wydarzenie',
  '/events/categories': 'Kategorie wydarzeń',
  '/events/register': 'Rejestracje na wydarzenia',
  '/special-offer': 'Oferty specjalne',
  '/special-offer/new': 'Dodaj ofertę',
  '/special-offer/categories': 'Kategorie ofert',
  '/spa': 'SPA',
  '/room-service': 'Room service',
  '/products-services': 'Sklep',
  '/product-rental': 'Wypożyczalnia',
  '/product-rental-reservations': 'Rezerwacje wypożyczalni',
  '/conferences': 'Konferencje',
  '/museum': 'Muzeum',
  '/orders': 'Zamówienia',
  '/order-products': 'Zamówienia sklepu',
  '/reports': 'Zgłoszenia',
  '/alerts': 'Alerty',
  '/marketing': 'Marketing',
  '/promo-codes': 'Kody rabatowe',
  '/welcome-promo-codes': 'Kody powitalne',
  '/subscribers': 'Subskrybenci',
  '/surveys': 'Ankiety',
  '/settings': 'Ustawienia',
  '/settings#general': 'Ogólne',
  '/settings#team': 'Zespół',
  '/settings#mobile-app': 'Aplikacja mobilna',
  '/settings#housekeeping': 'Housekeeping',
  '/settings#web-qr': 'Aplikacja webowa',
  '/settings#devices': 'Moje urządzenia',
  '/settings#danger': 'Strefa zagrożenia',
  '/my-profile': 'Mój profil',
  '/my-profile#profile': 'Dane profilowe',
  '/my-profile#account-info': 'Informacje o koncie',
  '/my-profile#security': 'Bezpieczeństwo',
  '/my-profile#language': 'Język aplikacji',
  '/my-profile#notifications': 'Powiadomienia',
  '/change-plan': 'Zmiana planu',
  '/faq-help': 'FAQ administratora',
  '/support-service': 'Wsparcie',
  '/hotel': 'Hotel',
  '/hotel/general-information': 'Informacje ogólne',
  '/hotel/amenities': 'Udogodnienia',
  '/hotel/business-card': 'Wizytówki',
  '/hotel/good-practice': 'Dobre praktyki',
  '/hotel/phonebook': 'Książka telefoniczna',
  '/hotel/wifi': 'Wi-Fi',
  '/hotel/regulations': 'Regulaminy',
  '/hotel/poi': 'Atrakcje',
  '/hotel/tv-chanel': 'Kanały TV',
  '/hotel/faq': 'FAQ dla gości',
  '/hotel/loyalty-program': 'Program lojalnościowy',
  '/hotel/social-media': 'Social media',
  '/hotel/virtual-tour': 'Wirtualny spacer',
  '/hotel/link-to-reviews': 'Linki do opinii',
  '/hotel/custom-blocks': 'Custom Blocks',
};

/**
 * Defensywny preprocessor: czasami AI mimo system promptu zwraca URL bez
 * markdownu (np. w backtickach \`/events/new\` lub goły /events/new w tekście).
 * Ta funkcja wykrywa takie wystąpienia i ZAMIENIA je na pełen markdown link
 * `[Nazwa](/path)`, używając PATH_LABELS jako źródła nazwy.
 *
 * Kolejność:
 * 1. URL w backtickach: \`/events/new\` → [Utwórz wydarzenie](/events/new)
 * 2. URL goły (poza już istniejącym markdownem): /events/new → [...](...)
 *    - Pomijamy ścieżki, które są już w (...) markdown linku
 */
const ensureLinksFormatted = (text: string): string => {
  let out = text;

  // Krok 1: URL w backtickach (np. `/events/new` lub `/settings#team`)
  out = out.replace(/`(\/[a-z0-9/#-]+)`/gi, (_match, path: string) => {
    const label = PATH_LABELS[path];
    if (label) return `[${label}](${path})`;
    return `[${path}](${path})`;
  });

  // Krok 2: URL goły. Używamy negative lookbehind przez zastąpienie po
  // tymczasowym maskowaniu istniejących markdown linków.
  // Maskujemy `[...](...)` zanim szukamy gołych URL.
  const masks: string[] = [];
  out = out.replace(/\[[^\]]+\]\([^)]+\)/g, (m) => {
    masks.push(m);
    return ` ${masks.length - 1} `;
  });

  // Teraz szukaj gołych URL (zaczynają się od /, kończą przed znakiem niedopasowanym).
  // Wspieramy fragment URL (#section) — `[a-z0-9/#-]*`.
  out = out.replace(/(^|[\s(])(\/[a-z0-9][a-z0-9/#-]*)(?=[\s.,!?)]|$)/gi, (_match, prefix: string, path: string) => {
    const label = PATH_LABELS[path];
    if (label) return `${prefix}[${label}](${path})`;
    // Nieznana ścieżka — zostaw jak jest (nie linkujemy zmyślonych).
    return `${prefix}${path}`;
  });

  // Odmaskuj.
  out = out.replace(/ (\d+) /g, (_m, idx: string) => masks[parseInt(idx, 10)] || '');

  return out;
};

/**
 * Linki w odpowiedziach AI: oznaczamy je atrybutem `data-internal-link="<path>"`
 * (dla wewnętrznych) lub zwykłym `target="_blank"` (dla zewnętrznych).
 * Click jest obsługiwany przez delegowany handler na kontenerze Messages —
 * eliminuje to problemy z closure / event bubbling przez dynamiczny content.
 */
const renderInline = (s: string): Array<string | ReactElement> => {
  const parts: Array<string | ReactElement> = [];
  let key = 0;
  const re = /(\[([^\]]+)\]\(([^)]+)\)|\*\*([^*]+)\*\*|`([^`]+)`)/g;
  let last = 0;
  let m: RegExpExecArray | null = re.exec(s);
  while (m) {
    if (m.index > last) parts.push(s.slice(last, m.index));
    if (m[2] && m[3]) {
      const text = m[2];
      const url = m[3];
      const isInternal = url.startsWith('/');
      if (isInternal) {
        parts.push(
          <a
            key={`l-${key++}`}
            href={url}
            data-internal-link={url}
          >
            {text}
          </a>,
        );
      } else {
        parts.push(
          <a
            key={`l-${key++}`}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {text}
          </a>,
        );
      }
    } else if (m[4]) {
      parts.push(<strong key={`b-${key++}`}>{m[4]}</strong>);
    } else if (m[5]) {
      parts.push(<code key={`c-${key++}`}>{m[5]}</code>);
    }
    last = re.lastIndex;
    m = re.exec(s);
  }
  if (last < s.length) parts.push(s.slice(last));
  return parts;
};

const renderMarkdown = (rawText: string): ReactElement[] => {
  // Defensywnie przetwarzamy tekst — zamieniamy "gołe" URL na pełne markdown
  // linki, żeby AI nie musiało być idealne.
  const text = ensureLinksFormatted(rawText);
  const blocks = text.split(/\n\n+/);
  return blocks.map((block, i) => {
    const lines = block.split('\n');
    const isOrdered = lines.every((l) => /^\d+\.\s/.test(l.trim()));
    const isUnordered = lines.every((l) => /^[-*]\s/.test(l.trim()));

    if (isOrdered && lines.length > 0) {
      return (
        <ol key={i}>
          {lines.map((l, j) => (
            <li key={j}>{renderInline(l.replace(/^\d+\.\s/, ''))}</li>
          ))}
        </ol>
      );
    }
    if (isUnordered && lines.length > 0) {
      return (
        <ul key={i}>
          {lines.map((l, j) => (
            <li key={j}>{renderInline(l.replace(/^[-*]\s/, ''))}</li>
          ))}
        </ul>
      );
    }
    return <p key={i}>{renderInline(block)}</p>;
  });
};

/* ============================================================
   Component
   ============================================================ */

// Przykładowe pytania na pustym czacie — przez `t()`, bo to tekst widoczny
// dla usera (wcześniej były zaszyte po polsku niezależnie od języka panelu).
const SUGGESTED_QUESTION_KEYS = [
  { key: 'helpAiChat.suggested1', fallback: 'Jak dodać wydarzenie?' },
  { key: 'helpAiChat.suggested2', fallback: 'Gdzie dodam ofertę specjalną?' },
  { key: 'helpAiChat.suggested3', fallback: 'Jak skonfigurować SPA?' },
];

// Persystencja historii konwersacji Stefana w localStorage.
// Klucz scopowany per-organizacja, żeby przełączenie hotelu nie pokazywało
// poprzedniej historii. Limit 100 ostatnich wiadomości — nie rośnie bez końca.
const STEFAN_HISTORY_KEY_PREFIX = 'stefan-chat-history';
const HISTORY_MAX_MESSAGES = 100;

const getHistoryKey = (orgId: string | null | undefined): string => `${STEFAN_HISTORY_KEY_PREFIX}:${orgId || 'default'}`;

const loadHistoryFromStorage = (orgId: string | null | undefined): ChatMessage[] => {
  try {
    const raw = localStorage.getItem(getHistoryKey(orgId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (m): m is ChatMessage => typeof m === 'object'
        && m !== null
        && typeof m.id === 'string'
        && (m.role === 'user' || m.role === 'assistant')
        && typeof m.content === 'string',
    );
  } catch {
    return [];
  }
};

const saveHistoryToStorage = (orgId: string | null | undefined, messages: ChatMessage[]): void => {
  try {
    const trimmed = messages.slice(-HISTORY_MAX_MESSAGES);
    localStorage.setItem(getHistoryKey(orgId), JSON.stringify(trimmed));
  } catch {
    // localStorage może być pełny / zablokowany — ignorujemy.
  }
};

interface HelpAiChatProps {
  /** Gdy podane — w headerze pojawia się guzik X zamykający panel. */
  onClose?: () => void;
}

export const HelpAiChat: FC<HelpAiChatProps> = ({ onClose }) => {
  const { t, i18n } = useTranslation();
  const { organizationId } = useOrganizationState();
  const navigate = useLocaleNavigate();

  // Czat siedzi na Dashboardzie (mount = wejście na pulpit), więc grzejemy
  // instancję od razu — zanim hotelarz zdąży napisać pierwsze pytanie.
  useWarmup('v3-panelHelpChat');

  const [messages, setMessages] = useState<ChatMessage[]>(() => loadHistoryFromStorage(organizationId));
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Przy zmianie organizacji (przełączenie hotelu) — załaduj historię nowej org.
  useEffect(() => {
    setMessages(loadHistoryFromStorage(organizationId));
  }, [organizationId]);

  // Persystuj historię przy każdej zmianie.
  useEffect(() => {
    saveHistoryToStorage(organizationId, messages);
  }, [organizationId, messages]);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  // Auto-grow textarea
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 88)}px`;
  }, [input]);

  // Backend płaci z puli organizacji i bez `organizationId` zwraca 400, więc czekamy na organizację.
  // Ten sam stan blokuje przycisk, Enter i podpowiedzi.
  const canSend = !isLoading && !!organizationId;

  const sendMessage = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || !canSend || !organizationId) return;

    setError(null);
    const userMsg: ChatMessage = { id: `u-${Date.now()}`, role: 'user', content: trimmed };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setIsLoading(true);

    try {
      // Backend `panelHelpChat` ma limit historii 20 wiadomości — wysyłamy
      // tylko ostatnie 20. Pełna historia (do 100) jest trzymana lokalnie
      // dla UX (gość widzi cały kontekst), ale do LLM idzie skrócony zestaw.
      const recentHistory = messages.slice(-20);
      const res = await axiosInstance.post<ApiResponse>('/v3-panelHelpChat', {
        message: trimmed,
        history: recentHistory.map((m) => ({ role: m.role, content: m.content })),
        organizationId,
        language: (i18n.language || 'pl').split('-')[0],
      });

      if (!res.data.success || !res.data.data?.answer) {
        throw new Error(res.data.error || 'Empty response');
      }

      const assistantMsg: ChatMessage = {
        id: `a-${Date.now()}`,
        role: 'assistant',
        content: res.data.data.answer,
      };
      setMessages((prev) => [...prev, assistantMsg]);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Unknown error';
      setError(isAiPoolRequiredError(e)
        ? aiPoolRequiredMessage(t)
        : t('helpAiChat.errorAnswer', 'Nie udało się uzyskać odpowiedzi. Spróbuj ponownie.'));
      // eslint-disable-next-line no-console
      console.error('[HelpAiChat] send error', message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(input);
  };

  /** Czyści historię czatu — w pamięci i w localStorage. */
  const clearChat = () => {
    setMessages([]);
    setError(null);
    setInput('');
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.removeItem(getHistoryKey(organizationId));
      } catch {
        // ignored — np. Safari private mode
      }
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (canSend) sendMessage(input);
    }
  };

  // Delegowany click handler — łapie kliknięcia w dowolnego anchora wewnątrz
  // bańki czatu i nawiguje routerem zamiast pełnym page reload. Korzystamy z
  // `data-internal-link` ustawianego w renderze markdown (link wewnętrzny =
  // ścieżka z `/` na początku). External links (target=_blank) nie mają tego
  // atrybutu i przelatują przez handler bez zmian.
  const handleMessagesClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    const anchor = target.closest('a[data-internal-link]') as HTMLAnchorElement | null;
    if (!anchor) return;
    const path = anchor.getAttribute('data-internal-link');
    if (!path) return;
    e.preventDefault();
    navigate(path);
  };

  return (
    <Card>
      <Header>
        <HeaderTitle>
          <Sparkles aria-hidden="true" />
          {t('helpAiChat.headerTitle', 'Zapytaj AI')}
        </HeaderTitle>
        <HeaderIconButton
          type="button"
          onClick={clearChat}
          disabled={messages.length === 0}
          aria-label={t('helpAiChat.newChat', 'Nowa rozmowa')}
          title={t('helpAiChat.newChat', 'Nowa rozmowa')}
        >
          <SquarePen />
        </HeaderIconButton>
        {onClose && (
          <HeaderIconButton
            type="button"
            onClick={onClose}
            aria-label={t('common.close', 'Zamknij')}
            title={t('common.close', 'Zamknij')}
          >
            <X />
          </HeaderIconButton>
        )}
      </Header>

      <Intro>
        {t('helpAiChat.intro', 'Zapytaj o konfigurację panelu, zamówienia albo od czego zacząć. Odpowiem i wskażę, gdzie kliknąć.')}
      </Intro>

      <Messages ref={scrollRef} onClick={handleMessagesClick}>
        {/* Log automatycznych aktualizacji bazy wiedzy agenta (bez ręcznego „Aktualizuj”). */}
        <StefanKbUpdates />

        {messages.map((m) => (
          <MessageRow key={m.id} $role={m.role}>
            <Bubble $role={m.role}>
              {renderMarkdown(m.content)}
            </Bubble>
          </MessageRow>
        ))}

        {isLoading && (
          <MessageRow $role="assistant">
            <Bubble $role="assistant">
              <TypingDots>
                <span />
                <span />
                <span />
              </TypingDots>
            </Bubble>
          </MessageRow>
        )}

        {error && (
          <MessageRow $role="assistant">
            <Bubble $role="assistant" $error>
              {error}
            </Bubble>
          </MessageRow>
        )}
      </Messages>

      <ComposerArea>
        <ComposerCard onSubmit={handleSubmit}>
          {messages.length === 0 && !isLoading && (
            <Suggestions>
              {SUGGESTED_QUESTION_KEYS.map(({ key, fallback }) => {
                const question = t(key, fallback);
                return (
                  <SuggestionChip key={key} type="button" disabled={!canSend} onClick={() => sendMessage(question)}>
                    <Sparkles aria-hidden="true" />
                    {question}
                  </SuggestionChip>
                );
              })}
            </Suggestions>
          )}
          <TextArea
            ref={textareaRef}
            placeholder={t('helpAiChat.placeholder', 'Napisz pytanie…')}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isLoading}
            rows={1}
          />
          <ComposerFooter>
            <SendButton type="submit" disabled={!canSend || input.trim().length === 0} aria-label={t('helpAiChat.send', 'Wyślij')}>
              <ArrowUp />
            </SendButton>
          </ComposerFooter>
        </ComposerCard>
        <Disclaimer>
          {t('helpAiChat.disclaimer', 'AI może się pomylić. Ważne decyzje skonsultuj z nami:')}
          {' '}
          <LocaleLink to="/support-service">{t('helpAiChat.supportLink', 'zakładka Wsparcie')}</LocaleLink>
          .
        </Disclaimer>
      </ComposerArea>
    </Card>
  );
};
